import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import defaultProfile from '../assets/default-profile.png';
import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { collection, onSnapshot, doc, setDoc } from 'firebase/firestore';
import BoardCard from '../components/BoardCard';
import { firestore } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';
import { FunnelIcon } from '@heroicons/react/24/outline';
import FilterPill from '../components/FilterPill';

interface BoardMeta {
  id: string;
  title: string;
  description?: string;
  createdAt: number;
  createdBy: string;
  lastModified?: number;
  statuses?: string[];
  tags?: string[];
}

const BrainstormingListPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const authCtx=useContext(AuthContext);
  const currentUser=authCtx?.user;
  const [boards, setBoards] = useState<BoardMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<'dateNew' | 'dateOld' | 'titleAsc' | 'updatedNew' | 'mostActive'>('dateNew');
  const [statusFilter, setStatusFilter] = useState<string[]>([]); // 'active' | 'archived'
  const [dateFilter, setDateFilter] = useState<string>(''); // yyyy-MM-dd
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [newStatuses, setNewStatuses] = useState<string[]>(['active']);

  useEffect(() => {
    if (!teamId) return;
    const colRef = collection(firestore, 'boards', teamId, 'brainstorming');
    return onSnapshot(colRef, (snap) => {
      const list: BoardMeta[] = [];
      snap.forEach((d) => {
        if (d.id === 'data') return; // legacy default board
        const data = d.data() as any;
        const statuses: string[] = data.statuses ?? [data.archived ? 'archived' : 'active'];
        list.push({ id: d.id, title: data.title || '(Untitled)', description: data.description, createdAt: data.createdAt || 0, createdBy: data.createdBy || '', lastModified: data.lastModified, statuses, tags: data.tags });
      });
      list.sort((a, b) => b.createdAt - a.createdAt);
      setBoards(list);
      setLoading(false);
    });
  }, [teamId]);

  // Derived filtered + sorted list
  const filteredBoards = useMemo(() => {
    let list = boards.filter((b) => {
      const q = searchTerm.toLowerCase();
      return (
        b.title.toLowerCase().includes(q) ||
        (b.description || '').toLowerCase().includes(q)
      );
    });

    // Apply status filter (multi-select)
    if (statusFilter.length > 0) {
      list = list.filter((b) => statusFilter.some((s)=> b.statuses?.includes(s)));
    }

    // Apply date filter
    if (dateFilter) {
      list = list.filter((b) => {
        const d = new Date(b.createdAt);
        const iso = d.toISOString().slice(0,10);
        return iso === dateFilter;
      });
    }

    // Create a new array to avoid mutating original state
    list = [...list];

    switch (sortOption) {
      case 'titleAsc':
        list.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'dateOld':
        list.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case 'updatedNew':
        list.sort((a, b) => (b.lastModified ?? b.createdAt) - (a.lastModified ?? a.createdAt));
        break;
      case 'mostActive':
        // For now treat same as updatedNew; extend later when we have activity metrics
        list.sort((a, b) => (b.lastModified ?? b.createdAt) - (a.lastModified ?? a.createdAt));
        break;
      case 'dateNew':
      default:
        list.sort((a, b) => b.createdAt - a.createdAt);
        break;
    }
    return list;
  }, [boards, searchTerm, sortOption, statusFilter, dateFilter]);


  const handleCreate = async () => {
    if (!teamId || !title.trim()) return;
    const id = uuidv4();
    const metaDoc = doc(firestore, 'boards', teamId, 'brainstorming', id);
    await setDoc(metaDoc, {
      id,
      title: title.trim(),
      description: description.trim(),
      createdAt: Date.now(),
      createdBy: 'system', // replace with current user id/email if available
      lastModified: Date.now(),
      statuses: newStatuses.length ? newStatuses : ['active'],
      tags: [],
      // empty initial board data
      nodes: {},
      stickyNotes: {},
      textBoxes: {},
      shapes: {},
      paths: {},
      images: {},
    });
    navigate(`/teams/${teamId}/brainstorming/${id}`);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Enhanced Header with TeamDetailsPage style */}
      <header className="relative bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 rounded-b-3xl pb-8 pt-6 px-6">
        {/* Back button */}
        <button
          onClick={() => navigate(`/teams/${teamId}`)}
          className="absolute top-6 left-6 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Title and description */}
        <div className="mt-12 text-center">
          <h1 className="text-3xl font-bold text-white font-['Poppins'] mb-2">Brainstorming Projects</h1>
          <p className="text-white/80 text-lg font-['Inter']">
            Create and manage your team's brainstorming sessions
          </p>
        </div>
      </header>

      <main className="flex-1 flex flex-col p-6">
        {/* Enhanced Toolbar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search input with enhanced styling */}
            <div className="relative flex-1 max-w-md">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-['Inter']"
              />
            </div>

            <div className="flex items-center gap-3">
              {/* Enhanced Sort dropdown */}
              <div className="relative">
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value as any)}
                  className="appearance-none border border-gray-200 rounded-xl px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-['Inter'] text-sm"
                >
                  <option value="dateNew">Newest first</option>
                  <option value="dateOld">Oldest first</option>
                  <option value="updatedNew">Last updated</option>
                  <option value="titleAsc">Title A-Z</option>
                  <option value="mostActive">Most active</option>
                </select>
                <svg className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Enhanced Filter button */}
              <button
                className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-50 transition-all duration-200 font-['Inter'] text-sm"
                onClick={() => setShowFilters((p) => !p)}
              >
                <FunnelIcon className="w-4 h-4 text-gray-600" />
                Filters
              </button>

              {/* Enhanced Summary chip */}
              {statusFilter.length + (dateFilter ? 1 : 0) > 0 && (
                <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                  {statusFilter.length + (dateFilter ? 1 : 0)} filter{statusFilter.length + (dateFilter ? 1 : 0) > 1 ? 's' : ''} applied
                </span>
              )}
            </div>
          </div>
        </div>

      {/* Enhanced Filter pills */}
      {(statusFilter.length > 0 || dateFilter) && (
        <div className="flex flex-wrap gap-2 mb-6">
          {statusFilter.map((s) => (
            <FilterPill key={s} label={`Status: ${s}`} onRemove={() => setStatusFilter((prev) => prev.filter((x) => x !== s))} />
          ))}
          {dateFilter && (
            <FilterPill key={dateFilter} label={`Date: ${dateFilter}`} onRemove={() => setDateFilter('')} />
          )}
        </div>
      )}

      {/* Enhanced Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6 space-y-6 transition-all">
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 font-['Poppins']">Status Filter</h3>
            <div className="flex flex-wrap gap-3">
              {['active','archived'].map((s) => {
                const checked = statusFilter.includes(s);
                return (
                  <label key={s} className={`px-4 py-2 rounded-xl cursor-pointer border-2 transition-all duration-200 font-['Inter'] text-sm ${
                    checked 
                      ? 'bg-blue-500 text-white border-blue-500 shadow-md' 
                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}> 
                    <input type="checkbox" className="hidden" checked={checked} onChange={()=>{
                      setStatusFilter((prev)=> checked ? prev.filter(x=>x!==s) : [...prev,s]);
                    }} /> 
                    {s.charAt(0).toUpperCase()+s.slice(1)} 
                  </label>
                );
              })}
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 font-['Poppins']">Date Filter</h3>
            <input 
              type="date" 
              value={dateFilter} 
              onChange={(e)=>setDateFilter(e.target.value)} 
              className="border-2 border-gray-200 px-4 py-3 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-['Inter']" 
            />
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button 
              className="text-sm text-red-600 hover:text-red-700 font-medium hover:underline transition-colors duration-200" 
              onClick={()=>{setStatusFilter([]);setDateFilter('');}}
            >
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {filteredBoards.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2 font-['Poppins']">No brainstorming projects yet</h3>
          <p className="text-gray-600 mb-4 font-['Inter']">Create your first brainstorming session to get started.</p>
          <p className="text-sm text-gray-500 font-['Inter']">Click the + button to create your first project.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBoards.map((b) => (
              <BoardCard key={b.id} board={b} teamId={teamId!} onClick={() => navigate(`/teams/${teamId}/brainstorming/${b.id}`)} />
            ))}
          </div>
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 font-['Poppins']">New Brainstorming Board</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-['Inter']">Title</label>
                <input 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-['Inter']" 
                  placeholder="Enter project title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-['Inter']">Description</label>
                <textarea 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-['Inter'] resize-none" 
                  rows={3}
                  placeholder="Describe your brainstorming project"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 font-['Inter']">Status</label>
                <div className="flex gap-3">
                  {['active','archived'].map((s)=>{
                    const checked = newStatuses.includes(s);
                    return (
                      <label key={s} className={`px-4 py-2 rounded-xl cursor-pointer border-2 transition-all duration-200 font-['Inter'] text-sm ${
                        checked 
                          ? 'bg-blue-500 text-white border-blue-500 shadow-md' 
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}>
                        <input type="checkbox" className="hidden" checked={checked} onChange={()=>{
                          setNewStatuses((prev)=> checked ? prev.filter(x=>x!==s) : [...prev,s]);
                        }} /> 
                        {s.charAt(0).toUpperCase()+s.slice(1)}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button 
                  className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium font-['Inter']" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium font-['Inter']" 
                  onClick={handleCreate} 
                  disabled={!title.trim()}
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Floating Action Button */}
      <button
        aria-label="Create brainstorming board"
        className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-3xl shadow-lg hover:shadow-xl hover:scale-110 focus:outline-none transition-all duration-300"
        onClick={() => setShowModal(true)}
      >
        +
      </button>
      </main>
      
      {/* Enhanced Footer */}
      <footer className="text-center text-sm text-gray-500 py-6 font-['Inter']">
        © {new Date().getFullYear()} StyleSync - Collaborative Design Platform
      </footer>
    </div>
  );
};

export default BrainstormingListPage; 